/**
 * Timezone-aware date utilities for streak calculation.
 * Uses the user's configured timezone or browser timezone.
 * Includes a 2-hour grace period past midnight so late-night
 * entries still count as "yesterday."
 */

const GRACE_HOURS = 2;

/**
 * Get the effective calendar date for a given timestamp in the user's timezone.
 * If the time is before the grace period (e.g., 1:30 AM), it's treated as the previous day.
 */
export function getEffectiveDate(
  date: Date | string,
  timezone?: string,
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Get hours in the user's timezone
  const hoursInTz = parseInt(
    d.toLocaleString("en-US", { timeZone: tz, hour: "numeric", hour12: false }),
    10,
  );

  // If within the grace period (midnight to GRACE_HOURS), subtract a day
  let adjusted = d;
  if (hoursInTz < GRACE_HOURS) {
    adjusted = new Date(d.getTime() - GRACE_HOURS * 60 * 60 * 1000);
  }

  // Format as YYYY-MM-DD in the user's timezone
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(adjusted);

  const year = parts.find((p) => p.type === "year")!.value;
  const month = parts.find((p) => p.type === "month")!.value;
  const day = parts.find((p) => p.type === "day")!.value;
  return `${year}-${month}-${day}`;
}

/**
 * Get today's effective date string in the user's timezone.
 */
export function getEffectiveToday(timezone?: string): string {
  return getEffectiveDate(new Date(), timezone);
}

/**
 * Calculate streak from an array of date strings (YYYY-MM-DD), assumed sorted descending.
 * Uses calendar-day diffing — consecutive days = streak continues.
 */
export function calculateStreakFromDates(dateStrings: string[]): number {
  if (dateStrings.length === 0) return 0;

  // Deduplicate (in case multiple sessions on same effective date)
  const unique = [...new Set(dateStrings)];
  if (unique.length === 0) return 0;

  let streak = 1;
  for (let i = 1; i < unique.length; i++) {
    const prev = new Date(unique[i - 1] + "T12:00:00Z"); // noon to avoid DST issues
    const curr = new Date(unique[i] + "T12:00:00Z");
    const diffDays = Math.round(
      (prev.getTime() - curr.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffDays === 1) {
      streak++;
    } else {
      break;
    }
  }

  return streak;
}
